import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  Chip,
  Alert,
  Snackbar,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import {
  getVolunteers,
  approveVolunteer,
  rejectVolunteer,
  revokeVolunteer,
  createVolunteerManually,
} from '../../../services/volunteerApi';
import { VolunteerUser } from '../../../types/volunteer';
import { formatDate } from '../../../utils/helpers';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`volunteer-tabpanel-${index}`}
      aria-labelledby={`volunteer-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `volunteer-tab-${index}`,
    'aria-controls': `volunteer-tabpanel-${index}`,
  };
}

const ManageVolunteersPage: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [volunteers, setVolunteers] = useState<VolunteerUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Create volunteer dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ email: '', firstName: '', lastName: '', phone: '' });
  const [isCreating, setIsCreating] = useState(false);

  const fetchVolunteers = useCallback(async () => {
    setLoading(true);
    try {
      if (tabValue === 2) {
        // Rejected and revoked share a tab. The backend has no 'revoked' filter (an unknown
        // status returns everyone), so fetch all and filter here.
        // ponytail: one unfiltered fetch of every volunteer; add a server-side filter if the list grows large.
        const res = await getVolunteers();
        setVolunteers(res.data.filter((v) => v.volunteerStatus === 'rejected' || v.volunteerStatus === 'revoked'));
      } else {
        const res = await getVolunteers({ status: tabValue === 0 ? 'pending' : 'approved' });
        setVolunteers(res.data);
      }
    } catch (err) {
      console.error('Failed to fetch volunteers:', err);
    } finally {
      setLoading(false);
    }
  }, [tabValue]);

  useEffect(() => {
    fetchVolunteers();
  }, [fetchVolunteers]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setSearchTerm('');
  };

  const handleApprove = async (userId: string) => {
    try {
      await approveVolunteer(userId);
      setSnackbar({ open: true, message: 'Volunteer approved successfully', severity: 'success' });
      fetchVolunteers();
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to approve volunteer', severity: 'error' });
    }
  };

  const handleReject = async (userId: string) => {
    try {
      await rejectVolunteer(userId);
      setSnackbar({ open: true, message: 'Volunteer rejected', severity: 'success' });
      fetchVolunteers();
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to reject volunteer', severity: 'error' });
    }
  };

  const handleRevoke = async (userId: string) => {
    try {
      await revokeVolunteer(userId);
      setSnackbar({ open: true, message: 'Volunteer access revoked', severity: 'success' });
      fetchVolunteers();
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to revoke volunteer', severity: 'error' });
    }
  };

  const handleCreateVolunteer = async () => {
    if (!createForm.email.trim()) return;
    setIsCreating(true);
    try {
      await createVolunteerManually({
        email: createForm.email.trim(),
        firstName: createForm.firstName.trim() || undefined,
        lastName: createForm.lastName.trim() || undefined,
        phone: createForm.phone.trim() || undefined,
      });
      setSnackbar({ open: true, message: 'Volunteer created successfully', severity: 'success' });
      setCreateDialogOpen(false);
      setCreateForm({ email: '', firstName: '', lastName: '', phone: '' });
      fetchVolunteers();
    } catch (err: any) {
      // Show the server's reason (e.g. names missing for a new login) rather than a generic failure.
      const data = err.response?.data;
      const detail = data?.details?.[0]?.message;
      const message = data?.error ? (detail ? `${data.error}: ${detail}` : data.error) : 'Failed to create volunteer';
      setSnackbar({ open: true, message, severity: 'error' });
    } finally {
      setIsCreating(false);
    }
  };

  const filteredVolunteers = volunteers.filter((v) => {
    const term = searchTerm.toLowerCase();
    return (
      `${v.firstName} ${v.lastName}`.toLowerCase().includes(term) ||
      v.email.toLowerCase().includes(term)
    );
  });

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" fontWeight={600}>
            Manage Volunteers
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Review applications and manage active volunteers
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddCircleIcon />}
          onClick={() => setCreateDialogOpen(true)}
        >
          Create Volunteer
        </Button>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="volunteer management tabs"
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '1rem',
            },
            '& .Mui-selected': {
              color: '#00b7ed',
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#00b7ed',
            },
          }}
        >
          <Tab label="Pending Applications" {...a11yProps(0)} />
          <Tab label="Active Volunteers" {...a11yProps(1)} />
          <Tab label="Rejected / Revoked" {...a11yProps(2)} />
        </Tabs>
      </Box>

      {/* Pending Applications Tab */}
      <TabPanel value={tabValue} index={0}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : filteredVolunteers.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
            <Typography color="text.secondary">No pending applications</Typography>
          </Paper>
        ) : (
          <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Phone</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Availability</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Experience</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Applied</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredVolunteers.map((volunteer) => (
                  <TableRow key={volunteer.id} hover>
                    <TableCell>
                      <Typography fontWeight={500}>
                        {volunteer.firstName} {volunteer.lastName}
                      </Typography>
                    </TableCell>
                    <TableCell>{volunteer.email}</TableCell>
                    <TableCell>{volunteer.phone || '-'}</TableCell>
                    <TableCell>{volunteer.volunteerAvailability || '-'}</TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          maxWidth: 200,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {volunteer.volunteerExperience || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {volunteer.volunteerAppliedAt
                        ? formatDate(volunteer.volunteerAppliedAt)
                        : '-'}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        color="success"
                        onClick={() => handleApprove(volunteer.id)}
                        title="Approve"
                      >
                        <CheckIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleReject(volunteer.id)}
                        title="Reject"
                      >
                        <CloseIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </TabPanel>

      {/* Active Volunteers Tab */}
      <TabPanel value={tabValue} index={1}>
        <TextField
          placeholder="Search by name or email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ mb: 3, maxWidth: 400 }}
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : filteredVolunteers.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
            <Typography color="text.secondary">No active volunteers found</Typography>
          </Paper>
        ) : (
          <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Phone</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Skills</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Total Shifts</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredVolunteers.map((volunteer) => (
                  <TableRow key={volunteer.id} hover>
                    <TableCell>
                      <Typography fontWeight={500}>
                        {volunteer.firstName} {volunteer.lastName}
                      </Typography>
                    </TableCell>
                    <TableCell>{volunteer.email}</TableCell>
                    <TableCell>{volunteer.phone || '-'}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {volunteer.volunteerSkills && volunteer.volunteerSkills.length > 0
                          ? volunteer.volunteerSkills.map((skill) => (
                              <Chip
                                key={skill.id}
                                label={skill.name}
                                size="small"
                                sx={{
                                  backgroundColor: skill.verified ? '#00b7ed' : undefined,
                                  color: skill.verified ? 'white' : undefined,
                                }}
                              />
                            ))
                          : <Typography variant="body2" color="text.secondary">None</Typography>}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {volunteer._count?.volunteerShiftRegistrations ?? 0}
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        color="error"
                        variant="outlined"
                        onClick={() => handleRevoke(volunteer.id)}
                      >
                        Revoke
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </TabPanel>

      {/* Rejected Tab */}
      <TabPanel value={tabValue} index={2}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : filteredVolunteers.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
            <Typography color="text.secondary">No rejected or revoked volunteers</Typography>
          </Paper>
        ) : (
          <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Phone</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Availability</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Experience</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Applied</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Reviewed</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredVolunteers.map((volunteer) => (
                  <TableRow key={volunteer.id} hover>
                    <TableCell>
                      <Typography fontWeight={500}>
                        {volunteer.firstName} {volunteer.lastName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {volunteer.volunteerStatus === 'revoked' ? (
                        <Chip label="Revoked" size="small" color="error" variant="outlined" />
                      ) : (
                        <Chip label="Rejected" size="small" />
                      )}
                    </TableCell>
                    <TableCell>{volunteer.email}</TableCell>
                    <TableCell>{volunteer.phone || '-'}</TableCell>
                    <TableCell>{volunteer.volunteerAvailability || '-'}</TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          maxWidth: 200,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {volunteer.volunteerExperience || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {volunteer.volunteerAppliedAt
                        ? formatDate(volunteer.volunteerAppliedAt)
                        : '-'}
                    </TableCell>
                    <TableCell>
                      {volunteer.volunteerReviewedAt
                        ? formatDate(volunteer.volunteerReviewedAt)
                        : '-'}
                    </TableCell>
                    <TableCell align="right">
                      {volunteer.volunteerStatus === 'rejected' && (
                        <Button
                          size="small"
                          color="success"
                          variant="outlined"
                          onClick={() => handleApprove(volunteer.id)}
                        >
                          Approve
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </TabPanel>

      {/* Create Volunteer Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create Volunteer</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            If someone already has an App login with this email, they will be set as a volunteer. Otherwise a new App login will be created, and first and last name are required. Being registered as a family in the CRM does not mean they have their own App login.
          </Typography>
          <TextField
            fullWidth
            label="Email"
            type="email"
            required
            value={createForm.email}
            onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
            sx={{ mb: 2, mt: 1 }}
          />
          <TextField
            fullWidth
            label="First Name (required for a new login)"
            value={createForm.firstName}
            onChange={(e) => setCreateForm({ ...createForm, firstName: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Last Name (required for a new login)"
            value={createForm.lastName}
            onChange={(e) => setCreateForm({ ...createForm, lastName: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Phone (optional)"
            value={createForm.phone}
            onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value })}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setCreateDialogOpen(false)} disabled={isCreating}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateVolunteer}
            disabled={!createForm.email.trim() || isCreating}
          >
            {isCreating ? 'Creating...' : 'Create Volunteer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ManageVolunteersPage;
